import * as React from 'react';

import type { BaseComponentProps } from '@coursera/cds-common';

import {
  useItemIndexInCollection,
  useItemsCollection,
  useRegisterItemInCollection,
} from 'bundles/browse-common/components/Pill/items-collection';
import BasePill from 'bundles/unified-common/components/pill/Pill';

export type Props = BaseComponentProps<'button'> &
  Pick<HTMLAnchorElement, 'href'> & {
    /**
     * Pill label to display.
     */
    children: React.ReactNode;

    /**
     * Invoked when pill is clicked.
     */
    onClick?: () => void;
  };

/**
 * Pills are compact, interactive components that enable users navigate content experience.
 */
const Pill = React.forwardRef<HTMLButtonElement, Props>(function Pill(props, ref) {
  const { children, id: idFromProps, ...buttonProps } = props;

  const {
    id,
    ref: groupItemRef,
    commonProps,
  } = useRegisterItemInCollection<HTMLButtonElement, Pick<Props, 'role'>, { id?: string }>(
    {
      id: idFromProps,
    },
    [idFromProps]
  );

  const role = commonProps?.role || props.role;

  const collection = useItemsCollection<HTMLButtonElement>();
  const indexInPillsGroup = useItemIndexInCollection(id);

  let tabIndex: number | undefined;
  if (collection && role === 'radio') {
    const groupHasSelectedPills = collection.items.some((item) => collection.props[item.id].selected);

    if (groupHasSelectedPills) {
      tabIndex = -1;
    } else {
      tabIndex = indexInPillsGroup === 0 ? 0 : -1;
    }
  }

  return (
    <BasePill ref={ref} refs={[groupItemRef]} tabIndex={tabIndex} {...buttonProps} id={idFromProps} role={role}>
      {children}
    </BasePill>
  );
});

export default Pill;
